import React from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import ListAltIcon from '@mui/icons-material/ListAlt';
import MapIcon from '@mui/icons-material/Map';
import { getCategoryColor } from './categoryPalette';

const MapControls = ({
  searchQuery,
  onSearchChange,
  onSearchSubmit,
  categories,
  selectedCategorySlugs,
  onToggleCategory,
  onClearCategories,
  onZoomIn,
  onZoomOut,
  onZoomReset,
  view,
  onViewChange,
}) => {
  return (
    <Box
      sx={{
        px: 2,
        py: 1.5,
        background: '#ffffff',
        borderBottom: '1px solid #ece7d8',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
      }}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ sm: 'center' }}>
        <TextField
          size="small"
          fullWidth
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onSearchSubmit?.();
            }
          }}
          placeholder="Search by vendor, category, or booth number"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => onSearchChange('')} aria-label="clear search">
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{ flex: 1 }}
        />

        <Stack direction="row" spacing={0.5} alignItems="center">
          <ToggleButtonGroup
            value={view}
            exclusive
            size="small"
            onChange={(_, value) => value && onViewChange(value)}
            aria-label="view mode"
          >
            <ToggleButton value="map" aria-label="map only" sx={{ px: 1.25 }}>
              <MapIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="split" aria-label="map with vendor list" sx={{ px: 1.25 }}>
              <ListAltIcon fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>

          <Tooltip title="Zoom out">
            <IconButton onClick={onZoomOut} size="small">
              <RemoveIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Reset view">
            <IconButton onClick={onZoomReset} size="small">
              <CenterFocusStrongIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Zoom in">
            <IconButton onClick={onZoomIn} size="small">
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {categories.length > 0 && (
        <Stack
          direction="row"
          spacing={0.75}
          sx={{
            overflowX: 'auto',
            pb: 0.5,
            // Hide scrollbar on mobile but keep it functional.
            '&::-webkit-scrollbar': { height: 4 },
            '&::-webkit-scrollbar-thumb': { background: '#ddd', borderRadius: 2 },
          }}
        >
          {selectedCategorySlugs.size > 0 && (
            <Chip
              label="Clear filters"
              size="small"
              variant="outlined"
              onClick={onClearCategories}
              sx={{ flexShrink: 0 }}
            />
          )}
          {categories.map((cat) => {
            const isSelected = selectedCategorySlugs.has(cat.slug);
            const color = getCategoryColor(cat.slug);
            return (
              <Chip
                key={cat.slug}
                label={`${cat.name}${cat.count ? ` (${cat.count})` : ''}`}
                size="small"
                onClick={() => onToggleCategory(cat.slug)}
                variant={isSelected ? 'filled' : 'outlined'}
                sx={{
                  flexShrink: 0,
                  borderColor: color,
                  color: isSelected ? '#fff' : color,
                  background: isSelected ? color : 'transparent',
                  fontWeight: isSelected ? 600 : 500,
                  '&:hover': {
                    background: isSelected ? color : `${color}22`,
                  },
                  '& .MuiChip-label': { px: 1 },
                }}
              />
            );
          })}
        </Stack>
      )}
    </Box>
  );
};

export default MapControls;
